import { TreeEntity } from '../../interfaces/TreeEntity';
import {
  CategoryResponse,
  CategoryStorePayload,
  CategoryUpdatePayload
} from './contracts';
import { deleteOptionalProperties } from '../../utils/helpers/helpers';
import axiosAPI from '../../utils/client/axiosAPI';
import { Attribute } from '../Attribute/Attribute';
import { Feature } from '../Feature/Feature';
import { DocumentResponse } from './contracts';
export class Category implements TreeEntity {
  public readonly id: number;
  public readonly parentId: number | null;
  public readonly name: string;
  public readonly price: number;
  public readonly penaltyPrice?: number;
  public readonly coverImage?: DocumentResponse | null;
  public readonly key?: number;
  public readonly value?: number;
  public readonly title?: string;
  public readonly children?: Category[];
  public readonly attributes?: Attribute[];
  public readonly features?: Feature[];
  public readonly productsCount: number = 0;

  public constructor({
    id,
    parent_id,
    name,
    price,
    penalty_price,
    cover_image,
    products_count,
    children,
    attributes,
    features
  }: CategoryResponse) {
    this.id = id;
    this.parentId = parent_id;
    this.name = name;
    this.key = id;
    this.value = id;
    this.price = price;
    this.penaltyPrice = penalty_price;
    this.coverImage = cover_image ?? null;
    this.productsCount = products_count ?? 0;
    this.title = name;
    this.children = children?.map((data) => new Category(data)) ?? [];
    this.attributes = attributes?.map((data) => new Attribute(data)) ?? [];
    this.features = features?.map((data) => new Feature(data)) ?? [];
  }
  public static async all(...relations: string[]): Promise<Category[]> {
    const { data } = await axiosAPI.get<CategoryResponse[]>(
      relations.length === 0
        ? '/categories'
        : `/categories?with=${relations.join(',')}`
    );
    return data.map((data) => new Category(data));
  }

  public static async store(payload: CategoryStorePayload): Promise<Category> {
    const formData = new FormData();
    formData.append('name', payload.name);
    formData.append('price', payload.price.toString());
    if (payload.parent_id) {
      formData.append('parent_id', payload.parent_id.toString());
    }
    if (payload.penalty_price !== undefined && payload.penalty_price !== null) {
      formData.append('penalty_price', payload.penalty_price.toString());
    }
    if (payload.cover_image_id) {
      formData.append('cover_image_id', payload.cover_image_id.toString());
    }
    const { data } = await axiosAPI.post<CategoryResponse>(
      'admin/categories',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return new Category(data);
  }

  public static toMap(categories: Category[]): Map<number, Category> {
    const result = new Map<number, Category>();
    const toMap = (categories: Category[]) => {
      categories.forEach((category) => {
        result.set(category.id, category);
        !!category.children && toMap(category.children);
      });
    };
    toMap(categories);
    return result;
  }

  public static async get(id: number | string): Promise<Category> {
    const { data } = await axiosAPI.get(`categories/${id}`);
    return new Category(data);
  }

  public async update(
    payload: CategoryUpdatePayload | FormData
  ): Promise<Category> {
    if (payload instanceof FormData) {
      const { data } = await axiosAPI.post(
        `admin/categories/${this.id}`,
        payload,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      return new Category(data);
    } else {
      const { data } = await axiosAPI.put(
        `admin/categories/${this.id}`,
        payload
      );
      return new Category(data);
    }
  }
  public static async delete(id: number): Promise<void> {
    await axiosAPI.delete(`admin/categories/${id}`);
  }
}
